/* De productfoto met genummerde aanwijsstippen.

   5 september 2026: de teksten per uitvoering staan onder Nederlandse
   sleutels (glad, gehamerd, klein-14-56-cm), maar de variant-picker geeft de
   vertaalde waarde terug. Op kopero.pt werd dus "liso" opgezocht in een kaart
   met "glad", waarna setVariant niets deed en de tekst op de eerste
   uitvoering bleef staan. De gekozen waarde gaat nu eerst door de kaart die
   kopero-product-gallery in de pagina zet. Zie kopero-uitvoering.

   4 oktober 2026: het beeld is nu de uitpakfoto in plaats van een illustratie. De stippen staan per
   foto op een eigen plek (de kaart in [data-kopero-beeld-kaart], veld "pins": laag:x:y|laag:x:y), en
   bij een gekozen stip wordt de rest van de foto gedimd rond die stip (spotlight). */
class KoperoAnatomie {
  constructor(root) {
    this.root = root;
    this.rows = [...root.querySelectorAll('[data-anatomie-row]')];
    this.hotspotLayer = root.querySelector('[data-anatomie-hotspots]');
    this.masker = root.querySelector('[data-anatomie-masker]');
    this.alles = root.querySelector('[data-anatomie-alles]');
    this.actief = -1;
    this.maskerTimer = null;
    this.label = root.querySelector('[data-anatomie-label]');
    this.variantCopy = this.parseVariantCopy();
    this.fotoKaart = this.parseFotoKaart();
    this.productHandle = root.dataset.productHandle;

    if (!this.rows.length || !this.hotspotLayer) return;

    this.root.dataset.enhanced = 'true';
    this.volgBovenrand();
    this.buildHotspots();
    this.rows.forEach((row, index) => {
      const trigger = row.querySelector('[data-anatomie-trigger]');
      trigger?.addEventListener('click', () => this.activate(index, true));
    });
    document.addEventListener('change', (event) => this.handleVariantInput(event));
    this.setVariant(this.root.dataset.currentVariant, false);
    this.reset();

    /* Terug naar alles helder: de knop op de foto, een tik op een kale plek van de foto, of Esc. */
    this.alles?.addEventListener('click', () => this.reset());
    this.root.querySelector('.kopero-anatomie__canvas')?.addEventListener('click', (event) => {
      if (this.actief < 0) return;
      if (event.target.closest('.kopero-anatomie__hotspot, [data-anatomie-alles]')) return;
      this.reset();
    });
    this.root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.actief >= 0) {
        this.reset();
        this.hotspots?.[0]?.focus({ preventScroll: true });
      }
    });
  }

  /* Op een telefoon plakt de foto onder de vaste balken bovenin (menu, sprongmenu). Hoe hoog die samen zijn
     weten alleen de pagina en de schermbreedte; --ka-top wordt daarom gemeten zolang dit blok in beeld is. */
  volgBovenrand() {
    const mq = window.matchMedia('(max-width: 899px)');
    const bovenranden = [document.getElementById('ke-kop'), document.querySelector('.ke-sprong')].filter(Boolean);
    const volgers = bovenranden.map((el) => el.classList.contains('ke-sprong') ? el.parentElement : el);
    let wacht = false;
    const meet = () => {
      wacht = false;
      if (!mq.matches) return;
      const r = this.root.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return;
      // Read the actual header/menu rectangles. Hit-testing can find this
      // section or a hidden header instead, leaving an outdated top offset.
      let onder = 0;
      bovenranden.forEach((el) => {
        const vak = el.getBoundingClientRect();
        if (vak.width && vak.height && vak.bottom > 0 && vak.top < innerHeight) {
          onder = Math.max(onder, vak.bottom);
        }
      });
      const top = `${Math.round(onder) + 8}px`;
      if (this.root.style.getPropertyValue('--ka-top') !== top) {
        this.root.style.setProperty('--ka-top', top);
      }
      // Follow only the short header/menu transition, including its last
      // frame, so no gap opens when the mobile header slides away.
      if (volgers.some((el) => el.getAnimations && el.getAnimations().some((a) => a.playState === 'running'))) plan();
    };
    const plan = () => {
      if (wacht) return;
      wacht = true;
      requestAnimationFrame(meet);
    };
    window.addEventListener('scroll', plan, { passive: true });
    window.addEventListener('resize', plan);
    volgers.forEach((el) => {
      el.addEventListener('transitionrun', plan);
      el.addEventListener('transitionend', plan);
      el.addEventListener('transitioncancel', plan);
    });
    if (window.ResizeObserver) {
      const observer = new ResizeObserver(plan);
      bovenranden.forEach((el) => observer.observe(el));
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(plan);
    plan();
  }

  /* Alles weer helder: geen spotlight, geen label, alle regels dicht, stippen pulseren weer. */
  reset() {
    this.actief = -1;
    this.root.classList.remove('is-engaged');
    delete this.root.dataset.activeLayer;
    if (this.label) this.label.textContent = '';
    if (this.alles) this.alles.hidden = true;
    this.rows.forEach((row) => {
      row.querySelector('[data-anatomie-trigger]')?.setAttribute('aria-expanded', 'false');
      const panel = row.querySelector('[data-anatomie-panel]');
      if (panel) panel.hidden = true;
    });
    this.hotspots?.forEach((hotspot) => hotspot.setAttribute('aria-pressed', 'false'));
  }

  parseVariantCopy() {
    try {
      return JSON.parse(this.root.querySelector('[data-anatomie-variant-copy]')?.textContent || '{}');
    } catch (error) {
      console.warn('Kopero variantteksten konden niet worden geladen.', error);
      return {};
    }
  }

  parseFotoKaart() {
    try {
      return JSON.parse(this.root.querySelector('[data-kopero-beeld-kaart]')?.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  normalizeVariant(value = '') {
    return value
      .toLocaleLowerCase('nl-NL')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  canoniek(sleutel) {
    try {
      const el = document.querySelector('[data-kopero-uitvoeringen]');
      if (!el) return sleutel;
      return JSON.parse(el.textContent || '{}')[sleutel] || sleutel;
    } catch (error) {
      return sleutel;
    }
  }

  handleVariantInput(event) {
    const picker = event.target.closest('variant-selects');
    if (!picker || picker.dataset.productHandle !== this.productHandle) return;

    const selected = [...picker.querySelectorAll('fieldset, .product-form__input--dropdown')]
      .map((group) => group.querySelector('input:checked, select')?.value)
      .filter(Boolean);

    if (selected.length) this.setVariant(this.canoniek(this.normalizeVariant(selected.join('-'))), true);
  }

  zetPositie(index, x, y) {
    const row = this.rows[index];
    const hotspot = this.hotspots?.[index];
    if (!row || !hotspot) return;
    row.dataset.x = x;
    row.dataset.y = y;
    hotspot.style.setProperty('--x', `${x}%`);
    hotspot.style.setProperty('--y', `${y}%`);
  }

  setVariant(variantKey, animate) {
    if (!variantKey) return;

    this.root.dataset.currentVariant = variantKey;
    const variantRows = this.variantCopy[variantKey] || {};

    this.rows.forEach((row) => {
      const copy = variantRows[row.dataset.layer];
      if (!copy) return;

      const title = row.querySelector('[data-anatomie-title]');
      const kicker = row.querySelector('[data-anatomie-kicker]');
      const body = row.querySelector('[data-anatomie-body]');
      if (copy.title && title) title.textContent = copy.title;
      if (copy.kicker && kicker) kicker.textContent = copy.kicker;
      if (copy.body && body) body.textContent = copy.body;
      row.dataset.title = copy.title || row.dataset.title;
    });

    const pins = (this.fotoKaart[variantKey] && this.fotoKaart[variantKey].pins) || '';
    const pinKaart = {};
    pins.split('|').forEach((p) => {
      const d = p.split(':');
      if (d.length === 3) pinKaart[d[0]] = [d[1], d[2]];
    });
    if (this.label) this.label.dataset.pos = pinKaart.labelpos ? pinKaart.labelpos[0] : 'm';

    this.hotspots?.forEach((hotspot, index) => {
      const titel = this.rows[index]?.dataset.title || `detail ${index + 1}`;
      hotspot.setAttribute('aria-label', `Bekijk: ${titel}`);
      hotspot.dataset.label = titel;

      const laag = this.rows[index]?.dataset.layer;
      if (laag && pinKaart[laag]) this.zetPositie(index, pinKaart[laag][0], pinKaart[laag][1]);
    });

    const actief = this.rows.findIndex((r) => r.dataset.layer === this.root.dataset.activeLayer);
    if (actief >= 0) {
      this.zetSpotlight(actief);
      if (this.label) this.label.textContent = this.rows[actief].dataset.title || '';
    }

    if (animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.root.querySelector('.kopero-anatomie__canvas')?.animate(
        [
          { opacity: 0.55, transform: 'scale(0.985)' },
          { opacity: 1, transform: 'scale(1)' },
        ],
        { duration: 300, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
      );
    }
  }

  buildHotspots() {
    this.rows.forEach((row, index) => {
      const button = document.createElement('button');
      const title = row.dataset.title || `Detail ${index + 1}`;

      button.type = 'button';
      button.className = 'kopero-anatomie__hotspot';
      button.style.setProperty('--x', `${row.dataset.x}%`);
      button.style.setProperty('--y', `${row.dataset.y}%`);
      button.style.setProperty('--delay', `${index * 180}ms`);
      button.dataset.index = index;
      button.dataset.label = title;
      button.setAttribute('aria-label', `Bekijk: ${title}`);
      button.setAttribute('aria-pressed', 'false');
      button.innerHTML = '<span aria-hidden="true"></span>';
      button.addEventListener('click', () => this.activate(index, true));
      this.hotspotLayer.appendChild(button);
    });

    this.hotspots = [...this.hotspotLayer.querySelectorAll('.kopero-anatomie__hotspot')];
    this.rows.forEach((row, index) => this.zetPositie(index, row.dataset.x, row.dataset.y));
  }

  zetSpotlight(index) {
    const row = this.rows[index];
    if (!row || !this.masker) return;
    const kaart = this.fotoKaart[this.root.dataset.currentVariant] || {};
    const maskers = kaart.masks || {};
    const d = maskers[row.dataset.layer] || maskers.product || '';
    const nieuw = d ? `M0 0H600V900H0Z${d}` : 'M0 0H600V900H0Z';
    if (this.masker.getAttribute('d') === nieuw) return;
    const rustig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    clearTimeout(this.maskerTimer);
    if (rustig) { this.masker.setAttribute('d', nieuw); return; }
    this.masker.style.opacity = '0';
    this.maskerTimer = setTimeout(() => {
      this.masker.setAttribute('d', nieuw);
      this.masker.style.opacity = '1';
    }, 170);
  }

  activate(index, wasInteraction) {
    const activeRow = this.rows[index];
    if (!activeRow) return;

    /* Dezelfde stip of regel nog een keer: terug naar alles helder. */
    if (wasInteraction && this.actief === index) {
      this.reset();
      return;
    }
    this.actief = index;
    if (this.alles) this.alles.hidden = !wasInteraction && !this.root.classList.contains('is-engaged');
    if (wasInteraction) {
      this.root.classList.add('is-engaged');
      if (this.alles) this.alles.hidden = false;
    }
    this.root.dataset.activeLayer = activeRow.dataset.layer;
    this.zetSpotlight(index);
    if (this.label) this.label.textContent = activeRow.dataset.title || '';

    this.rows.forEach((row, rowIndex) => {
      const isActive = rowIndex === index;
      const trigger = row.querySelector('[data-anatomie-trigger]');
      const panel = row.querySelector('[data-anatomie-panel]');

      trigger?.setAttribute('aria-expanded', String(isActive));
      if (panel) panel.hidden = !isActive;
    });

    this.hotspots?.forEach((hotspot, hotspotIndex) => {
      hotspot.setAttribute('aria-pressed', String(hotspotIndex === index));
    });

    /* Telefoon: de foto blijft staan, en de geopende regel scrolt altijd helemaal omhoog tot net onder de foto,
       zodat de bezoeker bovenaan begint met lezen. */
    if (wasInteraction && window.matchMedia('(max-width: 899px)').matches) {
      setTimeout(() => {
        const foto = this.root.querySelector('.kopero-anatomie__visual');
        const rij = activeRow.getBoundingClientRect();
        const rand = foto ? foto.getBoundingClientRect().bottom + 12 : 0;
        const delta = rij.top - rand;
        if (Math.abs(delta) > 1) window.scrollBy({ top: delta, behavior: 'smooth' });
      }, 60);
    }
  }
}

const initKoperoAnatomie = (scope = document) => {
  scope.querySelectorAll('[data-kopero-anatomie]:not([data-initialized])').forEach((root) => {
    root.dataset.initialized = 'true';
    new KoperoAnatomie(root);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initKoperoAnatomie(), { once: true });
} else {
  initKoperoAnatomie();
}

document.addEventListener('shopify:section:load', (event) => initKoperoAnatomie(event.target));
